import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import { throttle } from "lodash-es";
import { ChatMessage, Conversation } from "../../api/models";
import { getFrontEndSettings, historyList, historySearch } from "../../api";
import { checkAndHandleAuth } from "../../services/authService";

const SEARCH_MIN_LENGTH = 3;
const SEARCH_THROTTLE_MS = 3000;

export const useChatHistory = () => {
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [fetchingChatHistory, setFetchingChatHistory] = useState(false);
  const [fetchingConvMessages, setFetchingConvMessages] = useState(false);
  const [chatHistory, setChatHistory] = useState<Conversation[]>([]);
  const [selectedConvId, setSelectedConvId] = useState<string>("");
  const [clearing, setClearing] = useState(false);
  const [clearingError, setClearingError] = useState(false);
  const [toggleSpinner, setToggleSpinner] = useState(false);
  const [hideClearAllDialog, { toggle: toggleClearAllDialog }] =
    useBoolean(true);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState<Conversation[]>([]);
  const [searchingChatHistory, setSearchingChatHistory] = useState(false);

  const firstRender = useRef(true);
  const offsetRef = useRef(0);
  const fetchingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const historyGenerationRef = useRef(0);
  const searchFetchingRef = useRef(false);
  const latestSearchQueryRef = useRef("");
  const searchGenerationRef = useRef(0);

  useEffect(() => {
    if (firstRender.current && import.meta.env.MODE === "development") {
      firstRender.current = false;
      return;
    }
    (async () => {
      const response = await getFrontEndSettings();
      if (response.error) {
        checkAndHandleAuth();
      }
      if (response.CHAT_HISTORY_ENABLED) {
        handleFetchHistory();
      }
    })();
  }, []);

  const getMessagesByConvId = (id: string) => {
    const conv = chatHistory.find((obj) => String(obj.id) === String(id));
    if (conv) {
      return conv?.messages || [];
    }
    return [];
  };

  const getUseCaseByConvId = (id: string) => {
    const conv = chatHistory.find((obj) => String(obj.id) === String(id));
    if (conv) {
      return conv?.useCase || "";
    }
    return "";
  };

  const setMessagesByConvId = (id: string, messagesList: ChatMessage[]) => {
    const tempHistory = [...chatHistory];
    const matchedIndex = tempHistory.findIndex(
      (obj) => String(obj.id) === String(id),
    );
    if (matchedIndex > -1) {
      tempHistory[matchedIndex].messages = messagesList;
    }
  };

  const handleFetchHistory = useCallback(async () => {
    if (fetchingRef.current || !hasMoreRef.current) {
      return;
    }

    const requestGeneration = historyGenerationRef.current;
    fetchingRef.current = true;
    setFetchingChatHistory(true);

    try {
      const currentOffset = offsetRef.current;
      const response = await historyList(currentOffset);
      if (requestGeneration !== historyGenerationRef.current) return;

      if (response) {
        setChatHistory((prevData) => [
          ...prevData,
          ...response.conversations,
        ]);
        const newOffset = currentOffset + response.conversations.length;
        offsetRef.current = newOffset;
        const stillHasMore = newOffset < response.totalCount;
        hasMoreRef.current = stillHasMore;
      } else {
        setChatHistory([]);
        hasMoreRef.current = false;
      }
    } finally {
      if (requestGeneration === historyGenerationRef.current) {
        fetchingRef.current = false;
        setFetchingChatHistory(false);
      }
    }
  }, []);

  const resetHistory = useCallback(() => {
    historyGenerationRef.current += 1;
    fetchingRef.current = false;
    setFetchingChatHistory(false);
    setChatHistory([]);
    offsetRef.current = 0;
    hasMoreRef.current = true;
    handleFetchHistory();
  }, [handleFetchHistory]);

  const performSearch = useCallback(async (query: string) => {
    const trimmedQuery = query.trim();
    latestSearchQueryRef.current = trimmedQuery;

    if (trimmedQuery.length < SEARCH_MIN_LENGTH) {
      setIsSearchActive(false);
      setSearchResults([]);
      return;
    }

    if (searchFetchingRef.current) {
      return;
    }

    const requestGeneration = searchGenerationRef.current;
    searchFetchingRef.current = true;
    setIsSearchActive(true);
    setSearchingChatHistory(true);

    try {
      const response = await historySearch(trimmedQuery);

      if (
        requestGeneration !== searchGenerationRef.current ||
        latestSearchQueryRef.current !== trimmedQuery
      ) {
        return;
      }

      if (response) {
        setSearchResults(response.conversations);
      } else {
        setSearchResults([]);
      }
    } finally {
      searchFetchingRef.current = false;
      setSearchingChatHistory(false);
    }
  }, []);

  const throttledPerformSearch = useMemo(
    () =>
      throttle(performSearch, SEARCH_THROTTLE_MS, {
        leading: true,
        trailing: true,
      }),
    [performSearch],
  );

  const handleSearchHistory = useCallback(
    (query: string) => {
      const trimmedQuery = query.trim();
      if (trimmedQuery.length < SEARCH_MIN_LENGTH) {
        return;
      }
      setIsSearchActive(true);
      setSearchingChatHistory(true);
      throttledPerformSearch(query);
    },
    [throttledPerformSearch],
  );

  useEffect(() => {
    return () => {
      throttledPerformSearch.cancel();
    };
  }, [throttledPerformSearch]);

  const clearSearch = useCallback(() => {
    throttledPerformSearch.cancel();
    searchGenerationRef.current += 1;
    latestSearchQueryRef.current = "";
    setIsSearchActive(false);
    setSearchResults([]);
    setSearchingChatHistory(false);
  }, [throttledPerformSearch]);

  const onHistoryTitleChange = useCallback((id: string, newTitle: string) => {
    const tempChatHistory = [...chatHistory];
    const index = tempChatHistory.findIndex((obj) => obj.id === id);
    if (index > -1) {
      tempChatHistory[index].title = newTitle;
      setChatHistory(tempChatHistory);
    }

    if (isSearchActive) {
      setSearchResults((prevResults) => {
        const searchIndex = prevResults.findIndex((obj) => obj.id === id);
        if (searchIndex === -1) {
          return prevResults;
        }
        const updatedResults = [...prevResults];
        updatedResults[searchIndex].title = newTitle;
        return updatedResults;
      });
    }
  }, [chatHistory, isSearchActive]);

  const onHideClearAllDialog = () => {
    toggleClearAllDialog();
    setTimeout(() => {
      setClearingError(false);
    }, 2000);
  };

  return {
    chatHistory,
    clearing,
    clearSearch,
    clearingError,
    fetchingChatHistory,
    fetchingConvMessages,
    handleSearchHistory,
    hideClearAllDialog,
    isSearchActive,
    searchResults,
    searchingChatHistory,
    selectedConvId,
    showHistoryPanel,
    toggleSpinner,
    getMessagesByConvId,
    getUseCaseByConvId,
    handleFetchHistory,
    resetHistory,
    onHideClearAllDialog,
    onHistoryTitleChange,
    setChatHistory,
    setClearing,
    setClearingError,
    setFetchingConvMessages,
    setMessagesByConvId,
    setSearchResults,
    setSelectedConvId,
    setShowHistoryPanel,
    setToggleSpinner,
    toggleClearAllDialog,
  };
};
